import { normalizePath } from '../../_utils';
import type { ClawFileItem } from '../../types';
import type { PreviewType } from './types';

const ROOT_KEY = '__root__';
const MARKDOWN_FILE_EXTENSIONS = ['md', 'markdown', 'mdx'] as const;
const TEXT_PREVIEW_FILE_EXTENSIONS = [
  ...MARKDOWN_FILE_EXTENSIONS,
  'txt',
  'log',
  'json',
  'yaml',
  'yml',
  'xml',
  'csv',
] as const;
const IMAGE_FILE_EXTENSIONS = ['png', 'jpg', 'jpeg', 'gif', 'webp', 'bmp', 'svg'] as const;
const VIDEO_FILE_EXTENSIONS = ['mp4', 'webm', 'mov', 'm4v', 'avi'] as const;
const AUDIO_FILE_EXTENSIONS = ['mp3', 'wav', 'ogg', 'aac', 'm4a'] as const;

export function formatDate(dateString?: string): string {
  if (!dateString) return '-';
  const date = new Date(dateString);
  if (Number.isNaN(date.getTime())) return dateString;
  return date.toISOString().slice(0, 10);
}

export function getItemName(item: ClawFileItem): string {
  if (item.name?.trim()) return item.name.trim();
  const path = normalizePath(item.path);
  const segments = path.split('/').filter(Boolean);
  return segments[segments.length - 1] ?? '-';
}

export function getFolderKey(path?: string): string {
  const normalized = normalizePath(path);
  return normalized || ROOT_KEY;
}

export function isFolder(item: ClawFileItem): boolean {
  return item.type === 'folder';
}

export function sortItems(items: ClawFileItem[]): ClawFileItem[] {
  return [...items].sort((a, b) => {
    const folderDiff = Number(isFolder(b)) - Number(isFolder(a));
    if (folderDiff !== 0) return folderDiff;
    return getItemName(a).localeCompare(getItemName(b));
  });
}

export function getFileExtension(pathOrName: string): string {
  const value = normalizePath(pathOrName).split(/[?#]/)[0].toLowerCase();
  const lastDotIndex = value.lastIndexOf('.');
  if (lastDotIndex === -1) return '';
  return value.slice(lastDotIndex + 1);
}

export function isMarkdownFile(pathOrName?: string): boolean {
  const extension = getFileExtension(pathOrName || '');
  return (MARKDOWN_FILE_EXTENSIONS as readonly string[]).includes(extension);
}

export function isTextPreviewFile(pathOrName?: string): boolean {
  const extension = getFileExtension(pathOrName || '');
  return (TEXT_PREVIEW_FILE_EXTENSIONS as readonly string[]).includes(extension);
}

export function getPreviewTypeFromPath(pathOrName?: string): PreviewType {
  const extension = getFileExtension(pathOrName || '');
  if ((IMAGE_FILE_EXTENSIONS as readonly string[]).includes(extension)) return 'image';
  if ((VIDEO_FILE_EXTENSIONS as readonly string[]).includes(extension)) return 'video';
  if ((AUDIO_FILE_EXTENSIONS as readonly string[]).includes(extension)) return 'audio';
  if (extension === 'pdf') return 'pdf';
  if ((TEXT_PREVIEW_FILE_EXTENSIONS as readonly string[]).includes(extension)) return 'text';
  return 'unknown';
}

export function getPreviewType(item: ClawFileItem): PreviewType {
  return getPreviewTypeFromPath(item.name || item.path || '');
}
